import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Icon,
  SearchInput,
  Stack,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { AddIcon } from '@tonic-one/react-icons';
import { useTranslation } from 'react-i18next';
import Dropdown from '@Components/Common/Dropdown';
import CorrelatedRulesTable from './table';
import {
  AggressivityFilter,
  CorrelatedRule,
  CorrelatedRuleAggressivity,
  RuleType,
  RuleTypeFilter,
  ThreatTypeFilter,
} from '../types';
import {
  aggressivityFilterI18nKeyMap,
  aggressivityFilterOptions,
  AllAggressivity,
  filterSocialGraphRules,
} from '../utils';
import debounce from 'lodash/debounce';
import CustomRuleModal from './CustomRuleModal';
import isEqual from 'lodash/isEqual';
import { useSearchParams } from 'react-router-dom';
import { useRiskAIRulesQuery, useRuleMutations } from '../query';
import { useSocialGraphEnabledQuery } from '../../SocialGraph/query';

const CorrelatedRules = () => {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [searchParams, setSearchParams] = useSearchParams();
  const [MAX_CUSTOM_RULES] = useState(1000);
  const { createRuleMutation, editRuleMutation, deleteRuleMutation } =
    useRuleMutations();

  const [nameFilter, setNameFilter] = useState('');

  const [ruleTypeFilter, setRuleTypeFilter] = useState<RuleTypeFilter>(
    searchParams.get('ruleType')
      ? (searchParams.get('ruleType') as RuleTypeFilter)
      : RuleTypeFilter.All
  );
  const [ruleTypeFilterMap] = useState({
    [RuleTypeFilter.All]: t('common.label.all'),
    [RuleTypeFilter.UserDefined]: t(
      'globalSetting.correlatedRules.userDefined'
    ),
    [RuleTypeFilter.PreDefined]: t('globalSetting.correlatedRules.preDefined'),
  });

  const [aggressivityFilter, setAggressivityFilter] =
    useState<AggressivityFilter>(
      searchParams.get('aggressivityLevel')
        ? Number(searchParams.get('aggressivityLevel'))
        : AllAggressivity
    );
  const [threatTypeFilter, setThreatTypeFilter] =
    useState<ThreatTypeFilter>('All');
  const [isUserDefinedModalOpen, setIsUserDefinedModalOpen] = useState(false);

  const { data: _rules, isLoading: isLoadingRules } = useRiskAIRulesQuery();
  const { data: socialGraphEnabled, isLoading: isLoadingSocialGraph } =
    useSocialGraphEnabledQuery();

  const rules = useMemo(() => {
    if (!_rules) return undefined;
    return filterSocialGraphRules(_rules ?? [], socialGraphEnabled ?? false);
  }, [_rules, socialGraphEnabled]);

  const isLoading = isLoadingRules || isLoadingSocialGraph;

  const threatTypeFilterOptions = useMemo(() => {
    const options = new Set<string>();
    rules?.forEach((rule) => {
      if (rule.raiThreatType) options.add(rule.raiThreatType);
    });
    return ['All', ...Array.from(options)];
  }, [rules]);

  const isDuplicatedName = useCallback(
    (newRule: CorrelatedRule) => {
      return (
        (rules ?? []).filter(
          (r) =>
            r.raiRuleId !== newRule.raiRuleId &&
            r.raiRuleName?.trim() === newRule.raiRuleName.trim()
        ).length > 0
      );
    },
    [rules]
  );

  const ruleHasChanged = useCallback(
    (rule: CorrelatedRule) => {
      const originalRule = rules?.find((r) => r.raiRuleId === rule.raiRuleId);
      return !isEqual(originalRule, rule);
    },
    [rules]
  );

  const handleCreateRule = useCallback(
    async (rule: CorrelatedRule) => {
      if (isDuplicatedName(rule)) {
        window.showNotification({
          severity: 'error',
          titleMsg: t('globalSetting.correlatedRules.ruleExists'),
        });
        throw new Error(t('globalSetting.correlatedRules.ruleExists')); // to prevent closing the modal
      }
      await createRuleMutation.mutateAsync(rule);
    },
    [createRuleMutation, isDuplicatedName, t]
  );

  const handleEditRule = useCallback(
    async (rule: CorrelatedRule) => {
      if (ruleHasChanged(rule)) {
        if (isDuplicatedName(rule)) {
          window.showNotification({
            severity: 'error',
            titleMsg: t('globalSetting.correlatedRules.ruleExists'),
          });
          throw new Error(t('globalSetting.correlatedRules.ruleExists')); // to prevent closing the modal
        }
        await editRuleMutation.mutateAsync(rule);
      }
    },
    [editRuleMutation, isDuplicatedName, ruleHasChanged, t]
  );

  const debounceSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setNameFilter(value);
      }, 200),
    []
  );

  const customRulesNumber = useMemo(
    () =>
      rules?.filter((r) => r.raiRuleBuiltIn === RuleType.UserDefined).length ??
      0,
    [rules]
  );

  const handleSetAggressivityFilter = useCallback(
    (aggressivity: AggressivityFilter) => {
      const validAggressivity = Object.values(
        CorrelatedRuleAggressivity
      ).includes(aggressivity);
      if (validAggressivity && aggressivity !== AllAggressivity) {
        setSearchParams((params) => {
          params.set('aggressivityLevel', String(aggressivity));
          return params;
        });
        setAggressivityFilter(aggressivity);
      } else {
        setSearchParams((params) => {
          params.delete('aggressivityLevel');
          return params;
        });
        setAggressivityFilter(AllAggressivity);
      }
    },
    [setSearchParams]
  );

  const handleSetRuleTypeFilter = useCallback(
    (ruleTypeFilter: RuleTypeFilter) => {
      if (
        ![
          RuleTypeFilter.All,
          RuleTypeFilter.PreDefined,
          RuleTypeFilter.UserDefined,
        ].includes(ruleTypeFilter)
      ) {
        setRuleTypeFilter(RuleTypeFilter.All);
        setSearchParams((params) => {
          params.delete('ruleType');
          return params;
        });
        return;
      }
      setRuleTypeFilter(ruleTypeFilter);
      setSearchParams((params) => {
        params.set('ruleType', ruleTypeFilter);
        return params;
      });
      if (ruleTypeFilter === RuleTypeFilter.UserDefined) {
        handleSetAggressivityFilter(AllAggressivity);
      }
    },
    [handleSetAggressivityFilter, setSearchParams]
  );

  useEffect(() => {
    const aggressivityParam = searchParams.get('aggressivityLevel');
    const aggressivity = aggressivityParam ? Number(aggressivityParam) : -1;
    handleSetAggressivityFilter(aggressivity);
  }, [handleSetAggressivityFilter, searchParams, setSearchParams]);

  useEffect(() => {
    const ruleTypeParam = searchParams.get('ruleType');
    handleSetRuleTypeFilter(ruleTypeParam as RuleTypeFilter);
  }, [handleSetRuleTypeFilter, searchParams]);

  return (
    <Flex flexDirection="column" flex="auto">
      <Stack gap="1x" mb="4x">
        <Text color={colorStyle.color.secondary}>
          {t('globalSetting.correlatedRules.description')}
        </Text>
        <Flex columnGap="1x">
          <Text>
            {t('globalSetting.correlatedRules.description.noteLabel')}:
          </Text>
          <Text color={colorStyle.color.secondary}>
            {t('globalSetting.correlatedRules.description.note')}
          </Text>
        </Flex>
      </Stack>
      <Stack gap="2x" flex="auto" mb="4x">
        <Stack flexDirection="row" gap="2x" flexWrap="wrap">
          <Button
            variant="primary"
            gap="1x"
            width="max-content"
            onClick={() => setIsUserDefinedModalOpen(true)}
            disabled={customRulesNumber >= MAX_CUSTOM_RULES}
          >
            <Icon as={AddIcon} />
            {t('globalSetting.correlatedRules.addRule')}
          </Button>
          <Dropdown
            placeholder={t('globalSetting.correlatedRules.ruleType')}
            minWidth={188}
            maxWidth={225}
            value={ruleTypeFilter}
            options={Object.values(RuleTypeFilter)}
            renderOption={(option) => ruleTypeFilterMap[option]}
            onChange={(value: RuleTypeFilter) => handleSetRuleTypeFilter(value)}
          />
          <Dropdown
            placeholder={t(
              'globalSetting.correlatedRules.table.aggressivityLevel'
            )}
            width={225}
            value={`${aggressivityFilter}`}
            options={
              ruleTypeFilter === RuleTypeFilter.UserDefined
                ? [`${AllAggressivity}`]
                : aggressivityFilterOptions.map(String)
            }
            renderOption={(option) =>
              t(
                aggressivityFilterI18nKeyMap[
                  Number(option) as AggressivityFilter
                ]
              )
            }
            onChange={(value) => handleSetAggressivityFilter(Number(value))}
          />
          <Dropdown
            placeholder={t('globalSetting.correlatedRules.table.threatType')}
            width={300}
            value={threatTypeFilter}
            options={threatTypeFilterOptions}
            renderOption={(option) =>
              option === 'All' ? t('common.label.all') : option
            }
            onChange={setThreatTypeFilter}
          />
          <SearchInput
            width={300}
            placeholder={t('globalSetting.correlatedRules.searchRuleName')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              debounceSetSearch(e.target.value);
            }}
            onClearInput={() => setNameFilter('')}
          />
        </Stack>
        <CorrelatedRulesTable
          rules={rules ?? []}
          nameFilter={nameFilter}
          ruleTypeFilter={ruleTypeFilter}
          aggressivityFilter={aggressivityFilter}
          threatTypeFilter={threatTypeFilter}
          onCopyRule={handleCreateRule}
          onEditRule={handleEditRule}
          onDeleteRule={async (ruleId) => {
            await deleteRuleMutation.mutateAsync(ruleId);
          }}
          loading={isLoading}
        />
        {isUserDefinedModalOpen && (
          <CustomRuleModal
            isOpen={isUserDefinedModalOpen}
            onSave={async (rule) => {
              try {
                await handleCreateRule(rule);
                setIsUserDefinedModalOpen(false);
              } catch (e) {
                // User has to modify the rule name or retry
                console.error(e);
              }
            }}
            onClose={() => setIsUserDefinedModalOpen(false)}
          />
        )}
      </Stack>
    </Flex>
  );
};

export default CorrelatedRules;
